import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {
    ADD_NEW_CARD,
    AnnualPolicyInfo,
    FerryPaymentHistoryResponse,
    ICacheManager,
    INSTALMENT_STATUS_PENDING_PAYMENT,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE,
    INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    Mandate,
    NEW_OUTSTANDING_PAYMENT,
    Party,
    PaymentCard,
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_RENEWAL,
    PolicyPropertyEnum,
    ProductsEnum,
    Renewal,
    SETUP_DIRECT_DEBIT,
    Types,
    WOLF_MISSED_PAYMENT
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {PluginTemplates} from "../../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../../components/navigation/FalconHeader";
import {PaymentHistoryTable} from "../PaymentHistoryTable";
import {PaymentDaySection} from "../PaymentDaySection";
import {StorageSuccessModal} from "../../../../../../components/modals/StorageSuccessModal";
import {AutomaticRenewalCheckboxSection} from "./AutomaticRenewalCheckboxSection";
import {PaymentScheduleSection} from "./PaymentScheduleSection";
import {AccountBalanceSection} from "./AccountBalanceSection";
import {
    calculateInstallmentPayment,
    findMainAnnualPaymentCardByMainCardId,
    getAmountWithDecimalDigits,
    renderCardTypeName
} from "../../../../../../CommonFunctions";
import {CardExpiryBanner} from "../../../../../../components/banners/CardExpiryBanner";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../../../annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";
import {OverdueInstalmentBanner} from "../../../../../../components/banners/OverdueInstalmentBanner";
import {NextPaymentBanner} from "../../../../../../components/banners/NextPaymentBanner";
import {AnnualPaymentCardsSection} from "./AnnualPaymentCardsSection";
import {DirectDebitSection} from "./DirectDebitSection";
import {getLatestActivePaymentMandate, getLatestActiveRefundMandate} from "../../../../../../utils/MandateSelection";
import {InactiveDirectDebitBanner} from "../../../../../../components/banners/InactiveDirectDebitBanner";
import {OutstandingPaymentBanner} from "../../../../../../components/banners/OutstandingPaymentBanner";
import {pushEventToAdobeDataLayerTimeout} from "../../../../../../utils/AdobeDataLayerHelper";
import * as moment from "moment/moment";
import {Types as CustomerPortalTypes} from "../../../../../../Types";
import type {ICustomerPortalService} from "../../../../../../services/ICustomerPortalService";
import {CancellationBanner} from "../../../../../../components/banners/CancellationBanner";

interface IParams {
    policyNumber: string;
    [key: string]: string | undefined
}

interface IState {
    party?: Party;
    policy?: Policy;
    partyLoaded: boolean;
    instalmentsInfo?: InstalmentsInfo;
    showCardExpiryBanner: boolean;
    paymentDay: string;
    renewedPolicy: boolean;
    policiesProcessed: boolean;
    cardExpired?: boolean;
    mainCardId?: string;
    policyCancelled: boolean;
    isMobileScreenSize: boolean;
    reuseCard: boolean;
    automaticRenewal: boolean;
    performRenewal: boolean;
    renewalProposal?: any;
    paymentHistory?: FerryPaymentHistoryResponse;
    renewalProposalLoaded: boolean;
    currentPeriodPaymentHistory?: FerryPaymentHistoryResponse;
    pastPeriodPaymentHistory?: FerryPaymentHistoryResponse;
    showMissedPaymentBanner: boolean;
    refundMandate?: Mandate;
}

@authorize
export class MonthlyPaymentsDetailsPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {

    private readonly pluginTemplates = new PluginTemplates();
    private title: string;
    private modalContent: React.ReactNode;
    private anythingElseTitle: any;
    private explanationAnythingElseWording: any;
    private referrerJourney: string | null;
    private showSuccessPopup: string | null;
    private installmentSum: number = 0;
    private readonly policyNumber: string;
    private interestAmountWithInflight?: number;

    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private readonly cacheManager: ICacheManager;

    private pushAdobeDataLayerTimeout: any;

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            partyLoaded: false,
            showCardExpiryBanner: false,
            renewedPolicy: false,
            policiesProcessed: false,
            paymentDay: "",
            policyCancelled: true,
            isMobileScreenSize: window.innerWidth < 768,
            reuseCard: false,
            automaticRenewal: false,
            performRenewal: false,
            renewalProposalLoaded: false,
            showMissedPaymentBanner: false,
        };

        this.pluginTemplates.setPageTitle("Payment details");

        this.policyNumber = this.props.params.policyNumber;
    }

    public componentDidMount() {
        this.cacheManager.clearAll();

        this.showSuccessPopup = window.sessionStorage.getItem("showPopup");

        this.customerPortalService.getPolicy(this.policyNumber).subscribe((policy: Policy) => {
            const renewedPolicy: boolean = policy.policyHistoryList.filter(ph => ph.status === POLICY_STATUS_RENEWAL).length > 0;
            const cancelledPolicy: boolean = policy.policyHistoryList.filter(ph => ph.status === POLICY_STATUS_MTC).length > 0;
            let modifiedPaymentDay: string | undefined;

            policy.policyProperties.forEach(property => {
                if (property.key === PolicyPropertyEnum.ANNUAL_MONTHLY_OPTION_CARD_EXPIRED) {
                    this.setState({cardExpired: property.value === "true"});
                } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                    this.setState({mainCardId: property.value});
                } else if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                    this.setState({reuseCard: property.value === "true"})
                } else if (property.key === PolicyPropertyEnum.MODIFIED_PAYMENT_DAY) {
                    modifiedPaymentDay = property.value;
                } else if (property.key === PolicyPropertyEnum.PERFORM_RENEWAL) {
                    this.setState({performRenewal: property.value === "true"})
                } else if (property.key === PolicyPropertyEnum.AUTOMATIC_RENEWAL) {
                    this.setState({automaticRenewal: property.value === "true"})
                } else if (property.key === PolicyPropertyEnum.POLICY_DEBT) {
                    this.setState({showMissedPaymentBanner: property.value === "true"});
                }
            });

            const refundMandate: Mandate | undefined = getLatestActiveRefundMandate(policy.annualPolicyInfo?.mandates);

            this.setState({
                policy,
                renewedPolicy,
                policyCancelled: cancelledPolicy,
                policiesProcessed: true,
                paymentDay: modifiedPaymentDay ?? policy.interaction.paymentDay?.day,
                refundMandate
            })

            const instalmentsInfoRequest = renewedPolicy
                ? this.customerPortalService.getLastPolicyVersionInstalmentsInfo(this.policyNumber, false)
                : this.customerPortalService.getInstalmentsInfo(this.policyNumber, false);

            instalmentsInfoRequest.subscribe((instalmentsInfo: InstalmentsInfo) => {
                this.setState({
                    instalmentsInfo
                });
            });

            this.customerPortalService.getRenewalProposal(this.policyNumber).subscribe((renewalProposal: Renewal) => {
                this.setState({
                    renewalProposal,
                    renewalProposalLoaded: true
                })
            });

            if (renewedPolicy) {
                this.customerPortalService
                    .getCurrentPeriodPaymentHistory(this.policyNumber)
                    .subscribe((currentPeriodPaymentHistory: FerryPaymentHistoryResponse) => {
                        this.setState({
                            currentPeriodPaymentHistory,
                        });
                    });
                this.customerPortalService.getPastPeriodPaymentHistory(this.policyNumber).subscribe((pastPeriodPaymentHistory: FerryPaymentHistoryResponse) => {
                    this.setState({
                        pastPeriodPaymentHistory,
                    });
                });
            } else {
                this.customerPortalService.getPaymentHistory(this.policyNumber).subscribe((paymentHistory: FerryPaymentHistoryResponse) => {
                    this.setState({
                        paymentHistory,
                    });
                });
            }
        });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output,
                partyLoaded: true
            })
        });

        const successModalRef = window.sessionStorage.getItem("referrerJourney");
        if (this.showSuccessPopup) {
            if (successModalRef?.startsWith("mta")) {
                this.pushAdobeDataLayerTimeout = pushEventToAdobeDataLayerTimeout(2000, "mta_completed");
            }
        }
    }

    public componentWillUnmount() {
        clearTimeout(this.pushAdobeDataLayerTimeout);
    }

    public render() {
        if (!this.state.policy) {
            return <></>
        }

        const loading: boolean = !(this.state.partyLoaded && this.state.policiesProcessed && this.state.renewalProposalLoaded);
        const annualPolicyInfo: AnnualPolicyInfo = this.state.policy.annualPolicyInfo;
        const instalmentsInfo: InstalmentsInfo | undefined = this.state.instalmentsInfo;
        const canChangePaymentDate: boolean | undefined = annualPolicyInfo.paymentDayChangeAllowed;
        const isDirectDebitPaymentType: boolean = annualPolicyInfo.paymentType === "directDebit";

        this.setPopups();

        const lastPolicyInteraction = this.getLastPolicyInteraction();
        const showCardExpiryBanner: boolean = this.state.cardExpired !== undefined;

        const policy = this.state.policy
        const policyIsActive = moment(policy?.endDate).isSameOrAfter(moment()) && policy?.status !== POLICY_STATUS_MTC;
        const policyCancelled: boolean = policy?.status === POLICY_STATUS_MTC;
        let payOffInstallmentPresent: boolean = false;

        if (instalmentsInfo !== undefined && instalmentsInfo !== null) {
            payOffInstallmentPresent = instalmentsInfo.instalments.filter((instalment) => instalment.description.includes("PayOffBalance")).length > 0;
        }

        const mandate: Mandate | undefined = getLatestActivePaymentMandate(annualPolicyInfo?.mandates);
        const haveActiveMandate: boolean = !!mandate;

        this.installmentSum = calculateInstallmentPayment(this.state.instalmentsInfo?.instalments);
        const showOverdueInstalmentBanner: boolean = !!annualPolicyInfo && annualPolicyInfo.hasDebt && !annualPolicyInfo.unpaidRenewalInvoiceAmount;
        this.interestAmountWithInflight = this.getTotalInterestAmount(this.state.instalmentsInfo?.remainingInterest, this.state.instalmentsInfo?.inflightInterest);
        const hasUnpaidInstallmentIdToAmount = !!Object.keys(annualPolicyInfo?.unpaidInstallmentIdToAmount ?? {}).length;

        if (!loading && policy) {
            return (

                <div className="dashboard payment-details">

                    {this.showSuccessPopup &&
                        <StorageSuccessModal
                            title={this.title}
                            explanationWording={this.modalContent}
                            anythingElseTitle={this.anythingElseTitle}
                            explanationAnythingElseWording={this.explanationAnythingElseWording}
                            onClose={this.modalOnClose}
                        />
                    }

                    <FalconHeader/>

                    <div className="plugin">
                        <FalconNavigatorHeader
                            back={{
                                link: `/dashboard/annualMotor/${this.state.policy?.policyNumber}`,
                                hidden: !this.state.policy
                            }}
                            title="Payment details"
                        />
                        {this.state.policy && !!Object.keys(this.state.policy.annualPolicyInfo.unpaidChargebackInvoiceInfo ?? {}).length && this.referrerJourney !== NEW_OUTSTANDING_PAYMENT &&
                            <OutstandingPaymentBanner
                                unpaidChargebackInvoiceInfo={this.state.policy.annualPolicyInfo.unpaidChargebackInvoiceInfo}
                                policyNumber={this.state.policy.policyNumber}
                                product={this.state.policy.interaction.product}
                            />
                        }

                        {showOverdueInstalmentBanner && hasUnpaidInstallmentIdToAmount &&
                            <OverdueInstalmentBanner amount={annualPolicyInfo.overdueMonthlyInvoiceAmount}
                                                     unpaidInstallmentIdToAmount={annualPolicyInfo.unpaidInstallmentIdToAmount}
                                                     policyNumber={lastPolicyInteraction.agreementNumber}
                                                     product={ProductsEnum.WOLF_MOTOR}
                                                     ncdImpactedClaim={this.state.policy?.interaction.uistate.policyHasNcdImpactedClaims}
                                                     isDirectDebit={isDirectDebitPaymentType}
                                                     reuseCard={this.state.reuseCard}/>
                        }

                        {(!showOverdueInstalmentBanner || !annualPolicyInfo?.overdueMonthlyInvoiceAmount) && instalmentsInfo && (
                            <NextPaymentBanner
                                showInfoModal={false}
                                instalmentsInfo={instalmentsInfo}
                                smallScreenMarginRequired={true}
                            />
                        )}
                        {lastPolicyInteraction && isDirectDebitPaymentType && !haveActiveMandate && this.state.instalmentsInfo != null && Math.abs(this.state.instalmentsInfo.payOffAmount!) > 0 && (
                            <InactiveDirectDebitBanner policyNumber={this.policyNumber}
                                                       smallScreenMarginRequired={true}/>
                        )}

                        {policyCancelled && <CancellationBanner policy={policy} process={lastPolicyInteraction.process}/>}

                        {this.state.party && showCardExpiryBanner &&
                            <div className="inner-container">
                                <CardExpiryBanner
                                    party={this.state.party}
                                    policyNumber={this.policyNumber}
                                    annualMonthlyOptionCardExpired={this.state.cardExpired!}
                                />
                            </div>}

                        <div className="panel bg-white overflow-hidden p-0 font-weight-extra-light">
                            <div className="payment-details-table">

                                {this.state.policiesProcessed && this.state.instalmentsInfo?.remainingAmount !== 0 &&
                                    <div className="padding-32">
                                        <PaymentDaySection policyCancelled={this.state.policyCancelled}
                                                           renewalPaymentFailed={false}
                                                           interaction={lastPolicyInteraction}
                                                           paymentType="annual-product-monthly-payments"
                                                           paymentDay={this.state.paymentDay}
                                                           canChangePaymentDate={canChangePaymentDate}/>
                                    </div>
                                }

                                {this.state.policiesProcessed && isDirectDebitPaymentType && !payOffInstallmentPresent &&
                                    <div className="padding-32">
                                        <DirectDebitSection haveActiveMandate={haveActiveMandate}
                                                            policyNumber={this.props.params.policyNumber}
                                                            interaction={lastPolicyInteraction}
                                                            mandate={mandate}/>
                                    </div>
                                }

                                {this.state.policiesProcessed && this.state.refundMandate &&
                                    <div className="padding-32">
                                        <DirectDebitSection haveActiveMandate={true}
                                                            policyNumber={this.props.params.policyNumber}
                                                            interaction={lastPolicyInteraction}
                                                            mandate={this.state.refundMandate}
                                                            hasRefundMandate={true}/>
                                    </div>
                                }

                                <div className="padding-32">
                                    <AnnualPaymentCardsSection policyNumber={this.policyNumber}
                                                               payment="monthly"
                                                               handleReuseCardChange={this.handleReuseCardChange}
                                                               mainCardId={this.state.mainCardId}
                                                               annualPolicyInfo={annualPolicyInfo}
                                                               hasRefundMandate={!!this.state.refundMandate}
                                    />
                                </div>

                                {this.state.policiesProcessed && this.state.policy &&
                                    <div className="padding-32">
                                        <PaymentScheduleSection
                                            paidAmount={this.state.instalmentsInfo?.paidAmount}
                                            installmentSum={this.installmentSum}
                                            instalments={this.state.instalmentsInfo?.instalments || []}
                                            totalAmount={this.state.instalmentsInfo?.totalAmount}
                                            interaction={this.state.policy.interaction}
                                            isPaidOff={this.isInstalmentPlanPaidOff(this.state.instalmentsInfo)}
                                        />
                                    </div>
                                }

                                {lastPolicyInteraction != null
                                    && this.canPayOffBalance(this.state.instalmentsInfo) &&
                                    <div className="padding-32">
                                        <AccountBalanceSection
                                            interaction={lastPolicyInteraction}
                                            policyNumber={this.props.params.policyNumber}
                                            payOffAmountWithRemainingInterest={this.state.instalmentsInfo?.payOffAmount}
                                            remainingInterest={this.interestAmountWithInflight}
                                        />
                                    </div>
                                }

                                {!this.state.renewedPolicy && this.state.paymentHistory &&
                                    <div className="padding-32">
                                        <div className="blue-line"/>
                                        <PaymentHistoryTable
                                            paymentHistory={this.state.paymentHistory}
                                            policyNumber={this.props.params.policyNumber}
                                        />
                                    </div>
                                }

                                {this.state.renewedPolicy && (
                                    <>
                                        <div className="padding-32">
                                            {this.state.currentPeriodPaymentHistory && (
                                                <PaymentHistoryTable
                                                    title="Existing policy payment history"
                                                    paymentHistory={this.state.currentPeriodPaymentHistory}
                                                    policyNumber={this.props.params.policyNumber}
                                                />)}
                                        </div>
                                        <div className="padding-32">
                                            {this.state.pastPeriodPaymentHistory && (
                                                <PaymentHistoryTable
                                                    title="Old policies payment history"
                                                    paymentHistory={this.state.pastPeriodPaymentHistory}
                                                    policyNumber={this.props.params.policyNumber}
                                                />
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {policyIsActive &&
                            <AutomaticRenewalCheckboxSection
                                navigate={this.props.navigate}
                                activeAnnualPolicy={policy}
                                automaticRenewal={this.state.automaticRenewal}
                                performRenewal={this.state.performRenewal}
                                twoDaysGracePeriod={annualPolicyInfo.inGracePeriod}
                                renewalProposal={this.state.renewalProposal}
                            />}

                        {this.state.policy && this.pluginTemplates.renderBackLink("Back", false, `/dashboard/annualMotor/${this.state.policy.policyNumber}`)}
                    </div>

                    <Footer/>
                </div>);
        } else {
            return <Overlay show={true}/>
        }
    }

    private getLastPolicyInteraction(): any {
        return this.state.policy ? this.state.policy.interaction : null;
    }

    private modalOnClose(): void {
        window.sessionStorage.removeItem("paymentAmount");
        window.sessionStorage.removeItem("cardNumberObfuscated");
    }

    private readonly setPaymentDayChangePopup = (): void => {
        if (this.state.policiesProcessed && this.state.policy && this.state.paymentDay) {
            this.title = "All done!";
            this.modalContent =
                <div>
                    <p>
                        You have a new payment day,
                        every {PluginTemplates.convertWolfPaymentDate(this.state.paymentDay)} of the month.
                    </p>
                </div>


            this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
            this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
        }
    }

    private readonly setManualPaymentSuccessPopup = (): void => {
        let cardNumberObfuscated = window.sessionStorage.getItem("cardNumberObfuscated");

        cardNumberObfuscated ??= findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!)
            ?.cardNumberObfuscated!;

        this.title = "Payment was successful";
        this.modalContent = (
            <>
                <p>
                    <span className="font-weight-bold">
                        {window.sessionStorage.getItem("paymentAmount")}&nbsp;
                    </span> have been debited from your card ending <b>
                    {cardNumberObfuscated?.split('******')[1]} </b> to pay your overdue instalment.
                </p>
                <p>
                    Please allow a couple of minutes for the payment to be updated in your account.
                </p>
            </>
        );
        this.anythingElseTitle = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle();
        this.explanationAnythingElseWording = EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody();
    }

    private setNewCardPopup(): void {
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        if (paymentCard) {
            this.title = "All done!";
            this.modalContent =
                <p className="font-weight-bold">
                    You have a new payment method, {renderCardTypeName(paymentCard)} card ending
                    in {paymentCard.cardNumberObfuscated.split('******')[1]}
                </p>;
            this.anythingElseTitle = "";
            this.explanationAnythingElseWording = "";
        }

    }

    private setDirectDebitSetupPopup(): void {
        this.title = "All done!";
        this.modalContent =
            <p>
                Your new Direct Debit details have been updated along with your Direct Debit reference. You'll be able
                to see a new payment plan in your 'Documents' tile shortly.
            </p>;
        this.anythingElseTitle = "";
        this.explanationAnythingElseWording = "";
    }

    private setPayOffBalancePopup(): void {
        const paymentAmount: string | null = window.sessionStorage.getItem("paymentAmount");
        if (this.state.policiesProcessed && this.state.policy) {
            this.title = "Payment was successful";
            this.modalContent = (
                <>
                    <p className="mb-0">
                        {paymentAmount ?? getAmountWithDecimalDigits(this.state.policy.interaction.factors.payOffPremium)} has
                        been
                        taken out
                        from your account for paying off
                        your car insurance.
                    </p>
                    <p>
                        Please allow a couple of minutes for the payment to be updated in your account.
                    </p>
                </>
            )
        }
    }

    private setChargeBackPayoff(): void {
        window.sessionStorage.setItem("outstandingPaid", "true");
        const paymentAmount: string | null = window.sessionStorage.getItem("paymentAmount");
        const paymentCard: PaymentCard | null = findMainAnnualPaymentCardByMainCardId(this.state.party!, this.state.mainCardId!);
        if (this.state.policiesProcessed) {
            this.title = "Payment was successful";
            this.modalContent = (
                <p className="mb-0">
                    <span className="font-weight-bold">{paymentAmount}</span> has been debited from your card
                    ending <span
                    className="font-weight-bold">{paymentCard && Boolean(paymentCard) ? paymentCard.cardNumberObfuscated.split('******')[1] : '****'}</span> to
                    pay your <span className="font-weight-bold">outstanding balance</span>.
                </p>
            )
        }
    }


    private setPopups(): void {
        this.referrerJourney = window.sessionStorage.getItem("referrerJourney");

        if (this.state.partyLoaded && this.state.policy) {
            if (this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY) {
                this.setPaymentDayChangePopup();
            } else if (this.referrerJourney === WOLF_MISSED_PAYMENT) {
                this.setManualPaymentSuccessPopup();
            } else if (this.referrerJourney === ADD_NEW_CARD) {
                this.setNewCardPopup();
            } else if (this.referrerJourney === SETUP_DIRECT_DEBIT ||
                this.referrerJourney === INTERACTION_PROCESS_MTA_CHANGE_DIRECT_DEBIT) {
                this.setDirectDebitSetupPopup();
            } else if (this.referrerJourney === NEW_OUTSTANDING_PAYMENT) {
                this.setChargeBackPayoff();
            } else if (this.referrerJourney === INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE) {
                this.setPayOffBalancePopup();
            } else {
                window.sessionStorage.removeItem("showPopup");
                window.sessionStorage.removeItem("referrerJourney");
            }
        }
    }


    private readonly handleReuseCardChange = (reuseValue: boolean) => {
        this.setState({reuseCard: reuseValue})
    }

    private getTotalInterestAmount(interest?: number, inflightInterest?: number): number | undefined {
        if (inflightInterest !== undefined && interest !== undefined) {
            return inflightInterest + interest;
        }
        return interest;
    }

    private readonly isInstalmentPlanPaidOff = (instalmentsInfo?: InstalmentsInfo): boolean => {
        return !!instalmentsInfo && !this.hasOutstandingInstalment(instalmentsInfo);
    }

    private readonly canPayOffBalance = (instalmentsInfo?: InstalmentsInfo): boolean => {
        return (instalmentsInfo?.remainingPremium ?? 0) > 0 && (instalmentsInfo?.remainingAmount ?? 0) > 0;
    }

    private readonly hasOutstandingInstalment = (instalmentsInfo?: InstalmentsInfo): boolean => {
        return !!instalmentsInfo?.instalments.some(instalment =>
            instalment.status === INSTALMENT_STATUS_PENDING_PAYMENT && instalment.amount > 0
        );
    }

}

export const MonthlyPaymentsDetailsPage = withRouter<{}, IParams>(MonthlyPaymentsDetailsPagePlain)
